'use client'

import { useId } from 'react'
import './brand-logo.css'

/**
 * The Pixelmojo wordmark, inlined exactly as the reference shell draws it
 * (public/pixelmojo-rebuild/pixelmojo-home.html). The gradient id comes from
 * useId so the header and footer copies never collide. The pink is the logo's
 * own #F90B8A (the reference's #f80b8c was an approximation).
 */
export default function BrandLogo() {
  const gradientId = useId()
  const blueMaskId = `${gradientId}-blue`
  const yellowMaskId = `${gradientId}-yellow`
  const wordmarkMaskId = `${gradientId}-wordmark`
  return (
    <svg
      className='pm-brand-logo'
      viewBox='0 0 350 64.1'
      aria-hidden='true'
      focusable='false'
      onPointerEnter={event => {
        if (event.pointerType !== 'mouse') return
        event.currentTarget
          .getAnimations({ subtree: true })
          .forEach(animation => {
            animation.currentTime = 0
            animation.play()
          })
      }}
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1='347.66'
          x2='107.99'
          gradientUnits='userSpaceOnUse'
        >
          <stop offset='0' stopColor='#F90B8A' />
          <stop offset='1' stopColor='#008dcc' />
        </linearGradient>
        <mask
          id={blueMaskId}
          maskUnits='userSpaceOnUse'
          x='-1'
          y='-1'
          width='67'
          height='67'
        >
          <path
            className='pm-brand-logo__blue-draw'
            d='M-5-5 70 70'
            fill='none'
            stroke='white'
            strokeWidth='20'
            pathLength='1'
            strokeDasharray='1'
          />
        </mask>
        <mask
          id={yellowMaskId}
          maskUnits='userSpaceOnUse'
          x='-1'
          y='-1'
          width='67'
          height='67'
        >
          <path
            className='pm-brand-logo__yellow-draw'
            d='M-5 69 39 25'
            fill='none'
            stroke='white'
            strokeWidth='18'
            pathLength='1'
            strokeDasharray='1'
          />
        </mask>
        <mask
          id={wordmarkMaskId}
          maskUnits='userSpaceOnUse'
          x='86'
          y='0'
          width='266'
          height='65'
        >
          <rect
            className='pm-brand-logo__wordmark-draw'
            x='86'
            y='0'
            width='266'
            height='65'
            fill='white'
          />
        </mask>
      </defs>
      <path
        mask={`url(#${blueMaskId})`}
        fill='#008dcc'
        d='M0,10.75L10.67.07l53.46,53.46-10.68,10.68L0,10.75Z'
      />
      <path
        className='pm-brand-logo__spark'
        fill='#F90B8A'
        d='M64.15,10.76l-10.68,10.68-10.68-10.68L53.47.09l10.68,10.68h0Z'
      />
      <path
        className='pm-brand-logo__yellow'
        mask={`url(#${yellowMaskId})`}
        fill='#fdc304'
        d='M-.03,53.4l21.35-21.35,10.68,10.68-21.35,21.35L-.03,53.4Z'
      />
      <g fill={`url(#${gradientId})`} mask={`url(#${wordmarkMaskId})`}>
        <path d='M112.1,18.2c-2-1.1-4.1-1.6-6.4-1.6s-3.5.3-4.9,1-2.6,1.6-3.5,2.8h-.4l-.5-3.4h-7.7v44.1h9.1v-12.3c.6,0,1.1.1,1.8.2h1.9c3.8,0,6.9-.6,9.4-1.9,2.5-1.3,4.5-3.2,5.7-5.7,1.3-2.5,1.9-5.6,1.9-9.2s-.6-6.7-1.8-9-2.7-4-4.7-5c0,0,.1,0,.1,0ZM107.4,40c-1.3,1.4-3.3,2.1-5.9,2.1s-1.2,0-1.9-.1c-.7,0-1.2-.2-1.8-.3v-15.6c.7-.7,1.5-1.3,2.4-1.6.9-.3,1.9-.5,2.8-.5s2.4.3,3.3.8c1,.5,1.7,1.4,2.2,2.7.5,1.3.8,3.1.8,5.4,0,3.3-.7,5.7-2,7.1h.1Z' />
        <polygon points='168.6 17.1 158.8 17.1 152.7 26.2 146.4 17.1 136.3 17.1 147.1 32.7 136.1 48.5 146 48.5 152.5 39 158.9 48.5 169 48.5 157.9 32.6 168.6 17.1' />
        <path d='M191.5,18.5c-2-1.3-4.5-1.9-7.5-1.9s-5.7.6-7.9,1.9c-2.2,1.2-3.8,3.1-5,5.5-1.1,2.4-1.7,5.4-1.7,8.8s.6,6.9,1.9,9.3c1.2,2.4,3.1,4.2,5.7,5.3,2.5,1.1,5.8,1.7,9.7,1.7s3,0,4.5-.2,3.1-.4,4.8-.8v-6.9c-1.4.3-2.7.6-4.1.7-1.4.2-2.6.3-3.9.3-2.4,0-4.3-.2-5.7-.7s-2.3-1.3-2.9-2.5c-.5-.9-.7-2.1-.8-3.5h19.1v-2.4c0-3.6-.5-6.6-1.6-9-1-2.5-2.6-4.3-4.6-5.6ZM178.5,30.2c0-1.8.2-3.3.6-4.4.4-1.2,1-2.1,1.8-2.6s1.8-.8,2.9-.8,2.1.3,2.9.8c.8.5,1.4,1.4,1.7,2.6.3,1.1.5,2.6.6,4.4h-10.6.1Z' />
        <rect x='202.7' y='2.1' width='9.1' height='46.4' />
        <rect x='123.8' y='17.1' width='9.2' height='31.4' />
        <rect x='123.8' y='3.3' width='9.2' height='9.2' />
        <path d='M258.6,17.8c-1.6-.8-3.4-1.2-5.4-1.2s-3.6.4-5.3,1.1c-1.5.6-2.9,1.6-4.1,3-.8-1.3-1.8-2.2-3-2.9-1.5-.8-3.2-1.2-5-1.2s-3.4.3-4.9.9-2.8,1.5-3.9,2.8h-.4l-.6-3.2h-7.6v31.4h9.1v-21.4c.4-.6.9-1.1,1.5-1.5s1.2-.7,1.8-.9c.6-.2,1.3-.3,1.9-.3,1.2,0,2.2.4,2.9,1.1s1,2,1,3.9v19.1h9v-21.8c.3-.3.6-.7,1-.9.6-.4,1.2-.7,1.9-.9.7-.2,1.3-.3,2-.3,1.2,0,2.2.4,3,1.1s1.1,2,1.1,3.9v19.1h9.1v-19.6c0-3-.4-5.4-1.3-7.2s-2.1-3.1-3.7-3.9c0,0-.1-.2-.1-.2Z' />
        <path d='M292.4,18.4c-2.3-1.2-5-1.8-8.2-1.8s-5.9.6-8.2,1.8c-2.3,1.2-4,3-5.3,5.4-1.2,2.4-1.8,5.4-1.8,9.1s.6,6.6,1.9,9c1.2,2.4,3,4.2,5.3,5.4s5,1.8,8.1,1.8,5.9-.6,8.1-1.8c2.3-1.2,4.1-3,5.3-5.4,1.2-2.4,1.9-5.4,1.9-9.1s-.6-6.6-1.8-9c-1.2-2.4-3-4.2-5.3-5.4ZM289.5,38.5c-.5,1.4-1.2,2.3-2.1,2.9-.9.6-2,.8-3.3.8s-2.4-.3-3.3-.8c-.9-.6-1.6-1.5-2.1-2.9s-.7-3.2-.7-5.7.2-4.2.7-5.6,1.2-2.3,2.1-2.9,2-.8,3.3-.8,2.4.3,3.3.8c.9.6,1.6,1.5,2.1,2.9s.7,3.2.7,5.7-.2,4.2-.7,5.6Z' />
        <path d='M348.2,23.8c-1.2-2.4-3-4.2-5.3-5.4-2.3-1.2-5-1.8-8.2-1.8s-5.9.6-8.2,1.8c-2.3,1.2-4,3-5.3,5.4-1.2,2.4-1.8,5.4-1.8,9.1s.6,6.6,1.9,9c1.2,2.4,3,4.2,5.3,5.4s5,1.8,8.1,1.8,5.9-.6,8.1-1.8c2.3-1.2,4.1-3,5.3-5.4,1.2-2.4,1.9-5.4,1.9-9.1s-.6-6.6-1.8-9ZM340.1,38.5c-.5,1.4-1.2,2.3-2.1,2.9-.9.6-2,.8-3.3.8s-2.4-.3-3.3-.8c-.9-.6-1.6-1.5-2.1-2.9s-.7-3.2-.7-5.7.2-4.2.7-5.6,1.2-2.3,2.1-2.9,2-.8,3.3-.8,2.4.3,3.3.8c.9.6,1.6,1.5,2.1,2.9s.7,3.2.7,5.7-.2,4.2-.7,5.6Z' />
        <path d='M304.8,43.4c0,2.1-.1,3.9-.4,5.6-.3,1.7-.8,3.2-1.4,4.7-.7,1.5-1.5,2.9-2.7,4.4l5.9,3.9c1.9-2,3.4-4,4.6-6,1.1-2,2-4,2.5-6.1s.7-4.4.7-6.8v-26h-9.2v26.4-.1Z' />
        <rect x='304.8' y='3.3' width='9.2' height='9.2' />
      </g>
    </svg>
  )
}
